import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

import api from "../api/axiosInstance";
import { clearInviteToken, readInviteToken, useAuth } from "./AuthProvider";

const ProfileContext = createContext(null);

export const ProfileProvider = ({ children }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const keycloak = useAuth();

  const fetchProfile = useCallback(async () => {
    try {
      setLoading(true);
      const inviteToken = readInviteToken();
      const response = await api.get('/users/me', inviteToken ? { params: { inviteToken } } : undefined);
      // Only a brand-new user can claim it; either way it has been used.
      if (inviteToken) clearInviteToken();
      setProfile(response.data);

      // The backend may have just granted an admin realm role in Keycloak. The
      // access token already in hand was minted before that, so it still lacks
      // the role and admin-only UI (e.g. the manager dashboard) won't show up
      // until the token is refreshed to pick up the new role claim.
      if (response.data?.adminRoleJustAssigned && keycloak) {
        try {
          await keycloak.updateToken(-1);
          window.location.reload();
        } catch (refreshError) {
          console.error("Failed to refresh token after admin role assignment:", refreshError);
        }
      }
    } catch (error) {
      console.error("Failed to fetch user profile:", error);
      console.error("Error details:", error.response?.data);
      setProfile({});
    } finally {
      setLoading(false);
    }
  }, [keycloak]);

  const refreshProfile = useCallback(async () => {
    try {
      const response = await api.get('/users/me');
      setProfile(response.data);
    } catch (error) {
      console.error("Failed to refresh profile:", error);
    }
  }, []);

  useEffect(() => {
    if (!keycloak || !keycloak.authenticated) {
      console.log('Keycloak not ready yet');
      return;
    }

    console.log('Keycloak authenticated, fetching profile...');
    fetchProfile();
  }, [keycloak, keycloak?.authenticated, fetchProfile]);

  return (
    <ProfileContext.Provider
      value={{ profile, loading, refreshProfile }}
    >
      {children}
    </ProfileContext.Provider>
  );
};

export const useProfile = () => {
  const context = useContext(ProfileContext);
  return context?.profile;
};

export const useProfileContext = () => {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error('useProfileContext must be used within ProfileProvider');
  }
  return context;
};